// src/creator/creatorTripEngine.js
// ─────────────────────────────────────────────────────────────────────────────
// Turns a creator Trip Template into a bookable Trip Instance for one follower.
//   instance = template + { origin city, start date, travellers }
//
// Live parts (same backends the rest of the app uses):
//   - route search : ROUTE_SEARCH_WS_URL + buildTravelPayload. Modes default to Flight + Bus
//                    (creator trips); curated trips pass searchModes ["Train", "Flight", "Bus"].
//                    Split into direct + Mixed* messages exactly like SearchForm does.
//   - hotel search : POST {LAMBDA}/hotels (same call TripPlanner makes)
// Every live call has a timeout and an estimated fallback, so the page never
// breaks mid-demo. Fallback items are flagged `estimated: true` in the UI.
// Results are cached in sessionStorage so switching back to a city is instant.
// ─────────────────────────────────────────────────────────────────────────────

import { FEATURES, ROUTE_SEARCH_WS_URL } from "../config/features";
import { buildTravelPayload } from "../utils/buildTravelPayload";

const LAMBDA_BASE = "https://dw5wuw157e.execute-api.ap-south-1.amazonaws.com";
const IS_DEV = process.env.NODE_ENV === "development";
export const HOTEL_API = IS_DEV ? `${LAMBDA_BASE}/development` : LAMBDA_BASE;

export const PLATFORM_FEE = 249;
export const PLATFORM_GST = Math.round(PLATFORM_FEE * 0.18);

/* ── date helpers (all local dates, demo runs in IST) ───────────────────── */
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export const toDMY = (d) => `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
export const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const atTime = (d, hhmm) => { const [h, m] = hhmm.split(":").map(Number); const x = new Date(d); x.setHours(h, m, 0, 0); return x; };
export const fmtDay = (d) => d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
export const fmtTime = (d) => d ? new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "";
export const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

const haversineKm = (a, b) => {
    const R = 6371, rad = (x) => (x * Math.PI) / 180;
    const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
};

/* ── session cache ──────────────────────────────────────────────────────── */
const cacheGet = (k) => { try { const v = sessionStorage.getItem("rc_ct_" + k); return v ? JSON.parse(v) : null; } catch { return null; } };
const cacheSet = (k, v) => { try { sessionStorage.setItem("rc_ct_" + k, JSON.stringify(v)); } catch { /* full or blocked */ } };

/* ── route helpers (mirror SearchForm's getRoutePrice / segment shapes) ── */
const parsePrice = (v) => {
    if (v === undefined || v === null || v === "") return null;
    if (typeof v === "number") return v;
    const n = parseFloat(String(v).replace(/,/g, "").replace(/[^0-9.]/g, ""));
    return isNaN(n) ? null : n;
};

export function routeFare(route) {
    const root = parsePrice(route.total_fare ?? route.fare ?? route.price);
    if (root) return root;
    let sum = 0, found = false;
    for (const node of route.nodes || []) {
        const seg = node.segment || {};
        let v = parsePrice(seg.fare ?? seg.total_fare ?? seg.price);
        if (!v && seg.seat_costs) {
            v = parsePrice(seg.seat_class_ui && seg.seat_costs[seg.seat_class_ui]);
            if (!v) {
                const costs = Object.values(seg.seat_costs).map(parsePrice).filter((c) => c > 0);
                if (costs.length) v = Math.min(...costs);
            }
        }
        if (!v && seg.seat_avail_ui && typeof seg.seat_avail_ui === "object") {
            v = parsePrice(seg.seat_avail_ui.fare ?? seg.seat_avail_ui.ticket_fare ?? seg.seat_avail_ui.total_fare);
        }
        if (v) { sum += v; found = true; }
    }
    return found ? sum : null;
}

export const segMode = (seg) => {
    if (seg.flight_num || seg.carrier) return "flight";
    if (seg.bus_type || (seg.travel_name && !seg.train_name)) return "bus";
    if (seg.train_num || seg.train_name) return "train";
    return "bus";
};
export const routeDep = (r) => r.nodes?.[0]?.segment?.origin_dep_dt || null;
export const routeArr = (r) => { const s = r.nodes?.[r.nodes.length - 1]?.segment; return s?.dest_reach_dt || s?.dest_arr_dt || null; };
export const routeModes = (r) => [...new Set((r.nodes || []).map((n) => segMode(n.segment || {})))];
export const segName = (seg) => {
    const m = segMode(seg);
    if (m === "flight") return `${seg.carrier || "Flight"} ${seg.flight_num || ""}`.trim();
    if (m === "train") return `${(seg.train_name || "Train").trim()} ${seg.train_num ? "#" + seg.train_num : ""}`.trim();
    return `${seg.travel_name || "Bus"}${seg.bus_type ? " · " + seg.bus_type : ""}`;
};

/* ── live route search over the RouteChef WebSocket ─────────────────────── */
export const DEFAULT_SEARCH_MODES = ["Flight", "Bus"];

/* Same message split SearchForm.handleSearch uses for a layover search:
   message 1 = all direct modes, then one message per Mixed* combo. */
export function splitSearchModes(modes) {
    const t = modes.includes("Train"), f = modes.includes("Flight"), b = modes.includes("Bus");
    const direct = [...(t ? ["Train"] : []), ...(f ? ["Flight"] : []), ...(b ? ["Bus"] : [])];
    const mixed = [
        ...(t && f ? ["MixedTF"] : []),
        ...(f && b ? ["MixedFB", "MixedBF"] : []),
        ...(t && b ? ["MixedTB", "MixedBT"] : []),
    ];
    return [direct, ...mixed.map((m) => [m])].filter((m) => m.length);
}

function searchRoutesWS({ src, dest, date, pax, modes = DEFAULT_SEARCH_MODES, tag = "creator_trip_demo", timeoutMs = 30000 }) {
    return new Promise((resolve) => {
        const sessionId = Math.random().toString(36).substring(2, 10).padEnd(8, "0");
        const common = {
            src: { code: "", lat: src.lat, lng: src.lng },
            dest: { code: "", lat: dest.lat, lng: dest.lng },
            date,
            nonStop: false,
            prefTrainClass: "3A",
            prefFlightClass: "E",
            numAdults: pax,
            numChildren: 0,
            numInfants: 0,
            test: true,
            bus_test: FEATURES.BUS_TEST,
            flight_test: FEATURES.FLIGHT_TEST,
            src_name: src.name,
            dest_name: dest.name,
            from: `web_form_search:${tag}`,
            search_session_id: sessionId,
        };
        const msgs = splitSearchModes(modes).map((m) => buildTravelPayload({ ...common, modes: m }));

        let pending = msgs.length;
        const routes = [];
        let done = false;
        let ws;
        const finish = () => {
            if (done) return;
            done = true;
            try { ws && ws.close(); } catch { /* closing */ }
            resolve(routes);
        };
        const timer = setTimeout(finish, timeoutMs);

        try {
            ws = new WebSocket(ROUTE_SEARCH_WS_URL);
        } catch {
            clearTimeout(timer);
            return resolve([]);
        }
        ws.onopen = () => msgs.forEach((m, i) => setTimeout(() => { try { ws.send(JSON.stringify(m)); } catch { /* socket gone */ } }, i * 20));
        ws.onmessage = (ev) => {
            let data;
            try { data = JSON.parse(ev.data); } catch { return; }
            if (data?.data?.routes?.length) {
                for (const r of data.data.routes) if (!r.hide && r.nodes?.length) routes.push(r);
            }
            const terminal = (data?.status === 201 && data?.message === "Completed")
                || data?.status === 203 || (data?.message && String(data.message).includes("No routes found"))
                || data?.status === 500 || (data?.message && String(data.message).includes("flight_model"));
            if (terminal) {
                pending -= 1;
                if (pending <= 0) { clearTimeout(timer); finish(); }
            }
        };
        ws.onerror = () => { clearTimeout(timer); finish(); };
        ws.onclose = () => { clearTimeout(timer); finish(); };
    });
}

/* Estimated route when live search is off, slow or empty. Shape matches the
   WS route so the UI renders it the same way. */
function mockRoute({ src, dest, departAt, pax, mode, variant = 0 }) {
    const km = haversineKm(src, dest);
    const flight = mode ? mode === "flight" : km > 650;
    const durMins = flight ? Math.round(75 + km / 9) + 90 : Math.round((km / 48) * 60);
    const dep = new Date(departAt);
    const arr = new Date(dep.getTime() + durMins * 60000);
    const farePP = (flight ? Math.round((2800 + km * 2.4) / 50) * 50 : Math.round((250 + km * 1.55) / 10) * 10) + variant * (flight ? 650 : 120);
    return {
        route_id: `est_${src.name}_${dest.name}_${dep.getTime()}_${flight ? "f" : "b"}`,
        estimated: true,
        total_fare: farePP * pax,
        nodes: [{
            node_id: "n1",
            segment: flight
                ? { carrier: ["IndiGo", "Air India Express", "Akasa Air"][variant % 3], flight_num: "(est.)", origin_name: `${src.name} Airport`, dest_name: `${dest.name} Airport`, origin_dep_dt: dep.toISOString(), dest_arr_dt: arr.toISOString(), duration_mins: durMins }
                : { travel_name: ["IntrCity SmartBus", "Zingbus", "VRL Travels"][variant % 3] + " (est.)", bus_type: "A/C Sleeper (2+1)", origin_name: src.name, dest_name: dest.name, origin_dep_dt: dep.toISOString(), dest_arr_dt: arr.toISOString(), duration_mins: durMins },
        }],
    };
}

/* Several estimated options so the follower can still compare when offline. */
function mockOptions({ src, dest, anchor, pax }) {
    const km = haversineKm(src, dest);
    const h = 3600000;
    const out = [];
    if (km > 650) {
        out.push(mockRoute({ src, dest, departAt: anchor, pax, mode: "flight", variant: 0 }));
        out.push(mockRoute({ src, dest, departAt: new Date(anchor.getTime() + 3 * h), pax, mode: "flight", variant: 1 }));
        out.push(mockRoute({ src, dest, departAt: new Date(anchor.getTime() - 4 * h), pax, mode: "flight", variant: 2 }));
    }
    if (km < 1500) {
        out.push(mockRoute({ src, dest, departAt: new Date(anchor.getTime() - (km > 650 ? 10 : 0) * h), pax, mode: "bus", variant: 0 }));
        out.push(mockRoute({ src, dest, departAt: new Date(anchor.getTime() - (km > 650 ? 9 : -1) * h), pax, mode: "bus", variant: 1 }));
    }
    return out;
}

/* Get candidate routes for one edge leg: cache → live → estimate. */
async function edgeCandidates({ src, dest, date, pax, live, modes = DEFAULT_SEARCH_MODES, tag }) {
    const key = `r_${src.name}_${dest.lat}_${toISO(date)}_${pax}_${modes.join("")}`;
    const cached = cacheGet(key);
    if (cached) return { routes: cached, source: "cache" };
    if (live) {
        const routes = await searchRoutesWS({ src, dest, date, pax, modes, tag });
        const priced = routes.filter((r) => routeFare(r));
        if (priced.length) { cacheSet(key, priced.slice(0, 120)); return { routes: priced, source: "live" }; }
    }
    return { routes: [], source: "estimate" };
}

/* ── hotel search (same Lambda call TripPlanner makes) ──────────────────── */
async function searchHotels({ city, place, checkIn, checkOut, live }) {
    const key = `h_${city}_${toISO(checkIn)}_${toISO(checkOut)}`;
    const cached = cacheGet(key);
    if (cached) return { hotels: cached, source: "cache" };
    if (!live) return { hotels: [], source: "estimate" };
    try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 15000);
        let userId = "webbot_trip:creator_demo";
        try { userId = localStorage.getItem("routechef_user_id") || userId; } catch { /* blocked */ }
        const resp = await fetch(`${HOTEL_API}/hotels`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ place_name: place || city, check_in: toDMY(checkIn), check_out: toDMY(checkOut), radius_km: 20, user_id: userId }),
            signal: ctrl.signal,
        });
        clearTimeout(t);
        if (!resp.ok) return { hotels: [], source: "estimate" };
        const data = await resp.json();
        const hotels = (data.hotels || []).filter((h) => h.rooms?.length);
        if (hotels.length) cacheSet(key, hotels.slice(0, 25));
        return { hotels, source: hotels.length ? "live" : "estimate" };
    } catch {
        return { hotels: [], source: "estimate" };
    }
}

const roomFare = (r) => parsePrice(r?.total_fare ?? r?.TotalFare) || 0;

/* Apply the creator's lock rule to pick a hotel for a STAY block. */
function pickHotel(block, hotels, nights) {
    if (!hotels.length) return null;
    const withPrice = hotels
        .map((h) => {
            const room = [...(h.rooms || [])].sort((a, b) => roomFare(a) - roomFare(b))[0];
            return { ...h, room, perNight: roomFare(room) / Math.max(1, nights) };
        })
        .filter((h) => h.room && h.perNight > 0);
    if (!withPrice.length) return null;
    if (block.preferred) {
        const want = block.preferred.toLowerCase().split(" ").filter((w) => w.length > 3);
        const match = withPrice.find((h) => want.every((w) => (h.hotel_name || "").toLowerCase().includes(w)));
        if (match) return { ...match, matched: "preferred" };
    }
    const inTier = withPrice.filter((h) => h.perNight <= block.tier_max_pn);
    const pool = inTier.length ? inTier : withPrice;
    // best value: closest among the cheaper half
    const sorted = [...pool].sort((a, b) => a.perNight - b.perNight);
    const half = sorted.slice(0, Math.max(1, Math.ceil(sorted.length / 2)));
    const best = half.sort((a, b) => (a.distance_km ?? 99) - (b.distance_km ?? 99))[0];
    return { ...best, matched: block.preferred ? "substitute" : "best_value" };
}

/* Mode combo of a route, e.g. "flight", "bus", "bus+flight". Same key for both legs. */
export const routeModeKey = (r) => routeModes(r).join("+");

/* Keep the best N options of EACH mode combo, so cheap buses can never push
   every flight out of the list (and the mode tabs always show what was found).
   Input must already be sorted by score; output stays sorted by score. */
function topPerMode(sorted, perMode = 20) {
    const count = {};
    return sorted.filter((o) => {
        const k = routeModeKey(o.route);
        count[k] = (count[k] || 0) + 1;
        return count[k] <= perMode;
    });
}

/* ── the solver ─────────────────────────────────────────────────────────── */
/**
 * buildInstance(template, { origin, startDate, pax, live, routePref })
 * routePref: "best" | "cheapest" | "fastest"
 * Returns everything the page needs to render and price the trip.
 */
/**
 * onProgress(text)  one status line (creator page)
 * onStep({ step, state, count, source })  per part, for pages that show each search separately
 *   step: "out" | "back" | "stays"   state: "start" | "done" | "skipped"
 */
export async function buildInstance(trip, { origin, startDate, pax, live = true, routePref = "best", onProgress, onStep, searchModes = DEFAULT_SEARCH_MODES, searchTag = "creator_trip_demo" }) {
    const t0 = performance.now();
    const day1 = new Date(startDate); day1.setHours(0, 0, 0, 0);
    const rooms = Math.max(1, Math.ceil(pax / 2));
    const warnings = [];

    // Season check
    if (trip.season) {
        const md = `${String(day1.getMonth() + 1).padStart(2, "0")}-${String(day1.getDate()).padStart(2, "0")}`;
        const lastMd = (() => { const e = addDays(day1, trip.duration_days - 1); return `${String(e.getMonth() + 1).padStart(2, "0")}-${String(e.getDate()).padStart(2, "0")}`; })();
        const inWin = (x) => trip.season.from <= trip.season.to ? (x >= trip.season.from && x <= trip.season.to) : (x >= trip.season.from || x <= trip.season.to);
        if (!inWin(md) || !inWin(lastMd)) warnings.push({ kind: "season", text: `Outside the creator's season window. ${trip.season.note}` });
    }

    const sameCity = (a, b) => haversineKm(a, b) < 40;
    const entryPt = { name: trip.entry.city, lat: trip.entry.lat, lng: trip.entry.lng };
    const exitPt = { name: trip.exit.city, lat: trip.exit.lat, lng: trip.exit.lng };
    // Defaults keep templates without these fields from crashing the solver
    const startByHHMM = trip.entry.start_by || "12:00";
    const leaveAfterHHMM = trip.exit.leave_after || "13:00";
    const startBy = atTime(day1, startByHHMM);
    const returnDay = addDays(day1, trip.exit.day_offset);
    const leaveAfter = atTime(returnDay, leaveAfterHHMM);

    // Morning starts need the day-before window too (overnight bus or flight + arrival night)
    const morningStart = Number(startByHHMM.split(":")[0]) < 12;

    // ── 1. Getting there ──
    onProgress?.("Searching routes from " + origin.name);
    const outP = sameCity(origin, entryPt) ? Promise.resolve(null) : (async () => {
        const dates = morningStart ? [addDays(day1, -1), day1] : [day1];
        const results = await Promise.all(dates.map((d) => edgeCandidates({ src: origin, dest: entryPt, date: d, pax, live, modes: searchModes, tag: searchTag })));
        const source = results.some((r) => r.source === "live") ? "live" : results.some((r) => r.source === "cache") ? "cache" : "estimate";
        let cands = results.flatMap((r) => r.routes).filter((r) => routeArr(r) && new Date(routeArr(r)) <= startBy && new Date(routeArr(r)) >= atTime(addDays(day1, -1), "06:00"));
        if (!cands.length) {
            const longHaul = haversineKm(origin, entryPt) > 650;
            const departAt = morningStart
                ? atTime(addDays(day1, -1), longHaul ? "14:00" : "21:30") // flight the day before, or an overnight bus
                : atTime(day1, longHaul ? "07:00" : "05:00");
            const est = mockOptions({ src: origin, dest: entryPt, anchor: departAt, pax });
            const ok = est.filter((r) => new Date(routeArr(r)) <= startBy);
            cands = ok.length ? ok : est.slice(0, 1);
        }
        return { cands, source: cands[0].estimated ? "estimate" : source };
    })();

    // ── 2. Getting back ──
    const retP = sameCity(origin, exitPt) ? Promise.resolve(null) : (async () => {
        const r = await edgeCandidates({ src: exitPt, dest: origin, date: returnDay, pax, live, modes: searchModes, tag: searchTag });
        let cands = r.routes.filter((x) => routeDep(x) && new Date(routeDep(x)) >= leaveAfter);
        if (!cands.length) {
            const est = mockOptions({ src: exitPt, dest: origin, anchor: atTime(returnDay, leaveAfterHHMM < "18:00" ? "18:30" : "21:30"), pax });
            const ok = est.filter((x) => new Date(routeDep(x)) >= leaveAfter);
            cands = ok.length ? ok : est.slice(0, 1);
        }
        return { cands, source: cands[0].estimated ? "estimate" : r.source };
    })();

    // ── 3. Stays (all in parallel with the route searches) ──
    onProgress?.(`Searching routes from ${origin.name} and checking stays`);
    const stayBlocks = [];
    trip.days.forEach((d) => d.blocks.forEach((b) => { if (b.type === "STAY") stayBlocks.push({ ...b, dayIndex: d.day - 1 }); }));
    const staysP = Promise.all(stayBlocks.map(async (b) => {
        const checkIn = addDays(day1, b.dayIndex);
        const checkOut = addDays(checkIn, b.nights);
        const { hotels, source } = await searchHotels({ city: b.city, place: (trip.hotel_search_place || {})[b.city], checkIn, checkOut, live });
        const picked = pickHotel(b, hotels, b.nights);
        const total = picked ? roomFare(picked.room) * rooms : b.fallback_pn * b.nights * rooms;
        return {
            block: b, checkIn, checkOut, rooms, source: picked ? source : "estimate",
            hotel: picked, alternatives: hotels.length, total,
            name: picked ? (picked.hotel_name || `Hotel ${picked.hotel_code}`) : (b.preferred || `Best value stay in ${b.city}`),
            estimated: !picked,
        };
    }));

    const track = (step, p, summarise) => {
        onStep?.({ step, state: "start" });
        return p.then((v) => {
            try { onStep?.(v === null ? { step, state: "skipped" } : { step, state: "done", ...summarise(v) }); } catch { /* UI only */ }
            return v;
        });
    };
    const edgeSummary = (v) => ({ count: v.cands.filter((r) => !r.estimated).length, source: v.source });
    const [outRes, retRes, stays] = await Promise.all([
        track("out", outP, edgeSummary),
        track("back", retP, edgeSummary),
        track("stays", staysP, (list) => ({ count: list.filter((x) => x.hotel).length, source: list.every((x) => x.estimated) ? "estimate" : "live" })),
    ]);

    // ── pick edge routes ──
    const score = (r, arrivalNightCost = 0) => {
        const fare = routeFare(r) || 0;
        const dur = (new Date(routeArr(r)) - new Date(routeDep(r))) / 60000;
        if (routePref === "cheapest") return fare + arrivalNightCost;
        if (routePref === "fastest") return dur;
        return fare + arrivalNightCost + dur * 4; // ₹4 per minute of travel time
    };
    const arrivalNightPrice = 1800 * rooms; // simple estimate for an extra night at the entry point

    const dedupe = (arr) => {
        const seen = new Set();
        return arr.filter((o) => {
            const k = (o.route.nodes || []).map((n) => `${segName(n.segment || {})}|${n.segment?.origin_dep_dt}`).join(">");
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        });
    };
    const outOptions = !outRes ? [] : topPerMode(dedupe(outRes.cands.map((r) => {
        const needsNight = new Date(routeArr(r)) < day1; // lands the previous evening
        return { route: r, needsNight, fare: routeFare(r) || 0, s: score(r, needsNight ? arrivalNightPrice : 0) };
    }).sort((a, b) => a.s - b.s)));
    const retOptions = !retRes ? [] : topPerMode(dedupe(retRes.cands.map((r) => ({ route: r, fare: routeFare(r) || 0, s: score(r) }))
        .sort((a, b) => a.s - b.s)));

    // ── inner transport (fixed by creator, sized to group) ──
    const innerMoves = [];
    trip.days.forEach((d) => d.blocks.forEach((b) => { if (b.type === "MOVE") innerMoves.push({ ...b, dayIndex: d.day - 1, total: b.price_pp * pax }); }));

    return repriceInstance({
        trip, origin, day1, pax, rooms, stays, innerMoves, arrivalNightPrice,
        baseWarnings: warnings,
        outOptions, retOptions,
        outSource: outRes?.source, retSource: retRes?.source,
        outSkipped: !outRes, retSkipped: !retRes,
        ms: Math.round(performance.now() - t0),
    }, 0, 0);
}

/**
 * Re-price after the follower swaps the getting-there / getting-back option.
 * No network: everything needed is already on the instance.
 */
export function repriceInstance(base, outIdx = 0, retIdx = 0) {
    const { trip, origin, pax, stays, innerMoves, arrivalNightPrice, outOptions, retOptions } = base;
    const warnings = [...base.baseWarnings];

    let outbound = { skipped: true };
    if (!base.outSkipped && outOptions.length) {
        const o = outOptions[Math.min(outIdx, outOptions.length - 1)];
        outbound = { route: o.route, fare: o.fare, needsArrivalNight: o.needsNight, source: o.route.estimated ? "estimate" : base.outSource, alternatives: outOptions.length, idx: outIdx };
        if (o.needsNight) warnings.push({ kind: "align", text: `This option reaches ${trip.entry.city} the evening before, so we added one night there before Day 1.` });
    }
    let inbound = { skipped: true };
    if (!base.retSkipped && retOptions.length) {
        const o = retOptions[Math.min(retIdx, retOptions.length - 1)];
        inbound = { route: o.route, fare: o.fare, source: o.route.estimated ? "estimate" : base.retSource, alternatives: retOptions.length, idx: retIdx };
    }

    const lines = [];
    if (!outbound.skipped) lines.push({ k: "out", label: `Getting there from ${origin.name}`, amount: outbound.fare, estimated: outbound.source === "estimate" });
    if (outbound.needsArrivalNight) lines.push({ k: "arrnight", label: `Arrival night in ${trip.entry.city}`, amount: arrivalNightPrice, estimated: true });
    innerMoves.forEach((m, i) => lines.push({ k: "mv" + i, label: m.label, amount: m.total }));
    stays.forEach((s, i) => lines.push({ k: "st" + i, label: `${s.name}, ${s.block.nights}N`, amount: s.total, estimated: s.estimated }));
    if (!inbound.skipped) lines.push({ k: "ret", label: `Getting back to ${origin.name}`, amount: inbound.fare, estimated: inbound.source === "estimate" });

    const supplier = lines.reduce((a, l) => a + l.amount, 0);
    const markup = trip.creator_markup_pp * pax;
    const fees = PLATFORM_FEE + PLATFORM_GST;
    const total = supplier + markup + fees;

    return {
        ...base, outbound, inbound, warnings,
        price: { lines, supplier, markup, fees, total, perPerson: total / pax },
        anyEstimated: lines.some((l) => l.estimated),
    };
}
